import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Mail, Check, ArrowLeft } from "lucide-react";
import { LOGO_URL } from "../context/AuthContext";
import { authApi } from "../lib/api";
import { toast } from "sonner";

export default function ForgotPassword() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setBusy(true);
        try {
            await authApi.requestPasswordReset(email.trim().toLowerCase());
            setSent(true);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Could not send reset link",
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center px-4 py-8"
            data-testid="forgot-page"
        >
            <div className="w-full max-w-md">
                <div className="bg-[#E8241A] brut-border brut-shadow p-6 sm:p-8 mb-[-20px] relative z-10">
                    <img
                        src={LOGO_URL}
                        alt="911 Cellphone Bank"
                        className="w-full h-auto"
                    />
                </div>

                <div className="bg-white brut-border brut-shadow relative">
                    <span className="brut-tape">Password Reset</span>
                    <div className="p-6 sm:p-8 pt-10">
                        <h1 className="font-display text-3xl sm:text-4xl leading-none">
                            Forgot it?
                        </h1>
                        <p className="mt-3 text-sm">
                            Enter your work email and we'll send a one-time
                            link to reset your password.
                        </p>

                        {sent ? (
                            <div
                                className="mt-6 bg-[#FAFAF7] brut-border p-4 space-y-2"
                                data-testid="forgot-sent-card"
                            >
                                <div className="inline-flex items-center gap-2 font-display">
                                    <Check size={18} strokeWidth={3} />
                                    Check your inbox
                                </div>
                                <p className="text-xs">
                                    If <strong>{email}</strong> is an employee,
                                    a reset link is on its way. It expires in
                                    15 minutes.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setSent(false)}
                                    className="font-display text-xs underline"
                                    data-testid="forgot-sent-reset"
                                >
                                    Use a different email
                                </button>
                            </div>
                        ) : (
                            <form
                                onSubmit={handleSubmit}
                                className="mt-6 space-y-3"
                            >
                                <label className="block font-display text-[11px] tracking-widest">
                                    Work email
                                </label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    placeholder="you@911cellphonebank.org"
                                    className="w-full px-4 py-3 brut-border bg-white text-black placeholder:text-black/40 focus:bg-[#FAFAF7] outline-none"
                                    data-testid="forgot-email-input"
                                />
                                <button
                                    type="submit"
                                    disabled={busy}
                                    className="w-full bg-[#E8241A] text-white font-display text-lg sm:text-xl px-6 py-4 brut-border brut-shadow brut-press inline-flex items-center justify-center gap-2 disabled:opacity-60"
                                    data-testid="forgot-submit-btn"
                                >
                                    {busy ? "Sending…" : "Email Reset Link"}{" "}
                                    <Mail size={20} strokeWidth={3} />
                                </button>
                            </form>
                        )}

                        <div className="mt-5 text-center">
                            <button
                                type="button"
                                onClick={() => navigate("/login")}
                                className="inline-flex items-center gap-1.5 font-display text-xs tracking-widest underline"
                                data-testid="forgot-back-to-login"
                            >
                                <ArrowLeft size={12} strokeWidth={3} />
                                Back to sign-in
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
